import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageIntro, Empty } from "@/components/PageIntro";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";
import { fmtCurrencyFull, fmtCompact, fmtNumber } from "@/lib/format";
import { OwnerStatementDialog } from "@/components/OwnerStatement";

import { toCSV, downloadCSV } from "@/lib/format";
import { Download } from "lucide-react";
import { toast } from "sonner";

const CHART_COLORS = ["#3b82f6", "#10b981", "#f59e0b", "#f43f5e", "#06b6d4", "#a855f7"];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const profitClass = (v) => (v >= 0 ? "text-emerald-500" : "text-rose-500");
const sumBy = (rows, k) => rows.reduce((s, r) => s + (r[k] || 0), 0);

const OwnerKpis = ({ rows }) => {
  const items = [
    ["Revenue", sumBy(rows, "revenue"), "text-foreground", "border-l-sky-500"],
    ["Commission", sumBy(rows, "commission"), "text-emerald-600", "border-l-emerald-500"],
    ["Paid out (Vouchers + AdBlue + Exp.)", sumBy(rows, "vouchers") + sumBy(rows, "adblue") + sumBy(rows, "expenses"), "text-rose-500", "border-l-rose-500"],
    ["Net", sumBy(rows, "net"), sumBy(rows, "net") < 0 ? "text-rose-500" : "text-emerald-600", "border-l-amber-500"],
  ];
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 p-4 border-b border-border" data-testid="owner-kpis">
      {items.map(([l, v, c, b]) => (
        <div key={l} className={`rounded-lg border border-border border-l-4 ${b} bg-muted/30 px-4 py-3`}>
          <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">{l}</div>
          <div className={`font-mono font-bold text-lg mt-1 ${c}`}>{fmtCurrencyFull(v)}</div>
        </div>
      ))}
    </div>
  );
};

const OwnerTotals = ({ rows }) => (
  <tfoot className="sticky bottom-0 bg-muted font-bold border-t-2 border-border" data-testid="owner-perf-total">
    <tr>
      <td className="py-3 px-4 uppercase text-xs tracking-wider">Total · {rows.length} owners</td>
      <td className="py-3 px-4 text-right font-mono">{sumBy(rows, "trips")}</td>
      <td className="py-3 px-4 text-right font-mono">{fmtNumber(sumBy(rows, "quantity"))}</td>
      <td className="py-3 px-4 text-right font-mono">{fmtCurrencyFull(sumBy(rows, "revenue"))}</td>
      <td className="py-3 px-4 text-right font-mono text-emerald-600">{fmtCurrencyFull(sumBy(rows, "commission"))}</td>
      <td className="py-3 px-4 text-right font-mono text-rose-500">{fmtCurrencyFull(sumBy(rows, "vouchers"))}</td>
      <td className="py-3 px-4 text-right font-mono text-rose-500">{fmtCurrencyFull(sumBy(rows, "adblue"))}</td>
      <td className="py-3 px-4 text-right font-mono text-rose-500">{fmtCurrencyFull(sumBy(rows, "expenses"))}</td>
      <td className={`py-3 px-4 text-right font-mono ${profitClass(sumBy(rows, "net"))}`}>{fmtCurrencyFull(sumBy(rows, "net"))}</td>
    </tr>
  </tfoot>
);

export default function Reports() {
  const { selectedHub, year } = useApp();
  const [data, setData] = useState(null);
  const [filters, setFilters] = useState({ month: "all", start: "", end: "" });
  const [tick, setTick] = useState(0);
  const [reportOwner, setReportOwner] = useState(null);

  useEffect(() => {
    const params = { hub: selectedHub, year };
    if (filters.month !== "all") params.month = filters.month;
    if (filters.start) params.start = filters.start;
    if (filters.end) params.end = filters.end;
    api.get("/reports", { params }).then(({ data }) => setData(data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedHub, year, tick]);

  const applyFilters = () => setTick((t) => t + 1);
  const clearFilters = () => { setFilters({ month: "all", start: "", end: "" }); setTick((t) => t + 1); };

  if (!data) return <div className="py-20 text-center text-muted-foreground">Loading reports…</div>;

  const exportOwner = () => {
    const csv = toCSV(data.owner_performance, [
      { key: "name", label: "Owner" }, { key: "trips", label: "Trips" }, { key: "quantity", label: "Quantity (T)" },
      { key: "revenue", label: "Revenue" }, { key: "commission", label: "Commission" },
      { key: "vouchers", label: "Vouchers Paid" }, { key: "adblue", label: "AdBlue" }, { key: "expenses", label: "Expenses" }, { key: "net", label: "Net" },
    ]);
    downloadCSV(`owner-performance-${year}.csv`, csv);
    toast.success("Owner performance exported");
  };
  const exportTruck = () => {
    const csv = toCSV(data.truck_performance, [
      { key: "vehicle_number", label: "Vehicle" }, { key: "owner_name", label: "Owner" }, { key: "vehicle_type", label: "Type" },
      { key: "trips", label: "Trips" }, { key: "quantity", label: "Quantity (T)" }, { key: "revenue", label: "Revenue" }, { key: "commission", label: "Commission" },
    ]);
    downloadCSV(`truck-performance-${year}.csv`, csv);
    toast.success("Truck performance exported");
  };
  const exportHub = () => {
    const csv = toCSV(data.hub_profit, [
      { key: "hub", label: "Hub" }, { key: "revenue", label: "Revenue" }, { key: "expenses", label: "Expenses" }, { key: "profit", label: "Profit" },
    ]);
    downloadCSV(`hub-profit-${year}.csv`, csv);
    toast.success("Hub report exported");
  };

  const t = data.totals;

  return (
    <div>
      <PageIntro title="Reports" subtitle="Profit & loss analytics and exportable statements" />

      <div className="flex flex-wrap items-end gap-3 mb-6 bg-card border border-border rounded-xl p-4" data-testid="reports-filter-bar">
        <div className="flex flex-col gap-1">
          <label className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Month</label>
          <select data-testid="report-month-filter" value={filters.month} onChange={(e) => setFilters({ ...filters, month: e.target.value })} className="h-9 rounded-lg border border-border bg-background px-3 text-sm">
            <option value="all">All months ({year})</option>
            {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m} {year}</option>)}
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">From</label>
          <input type="date" data-testid="report-start-filter" value={filters.start} onChange={(e) => setFilters({ ...filters, start: e.target.value })} className="h-9 rounded-lg border border-border bg-background px-3 text-sm" />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">To</label>
          <input type="date" data-testid="report-end-filter" value={filters.end} onChange={(e) => setFilters({ ...filters, end: e.target.value })} className="h-9 rounded-lg border border-border bg-background px-3 text-sm" />
        </div>
        <Button size="sm" onClick={applyFilters} data-testid="report-apply-filters">Apply</Button>
        <Button size="sm" variant="outline" onClick={clearFilters} data-testid="report-clear-filters">Clear</Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <div className="bg-card border border-border rounded-xl p-4 shadow-sm">
          <div className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Revenue</div>
          <div className="mt-1 text-2xl font-extrabold font-mono text-emerald-500" data-testid="report-total-revenue">{fmtCompact(t.revenue)}</div>
        </div>
        <div className="bg-card border border-border rounded-xl p-4 shadow-sm">
          <div className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Commission</div>
          <div className="mt-1 text-2xl font-extrabold font-mono text-blue-500" data-testid="report-total-commission">{fmtCompact(t.commission)}</div>
        </div>
        <div className="bg-card border border-border rounded-xl p-4 shadow-sm">
          <div className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Expenses</div>
          <div className="mt-1 text-2xl font-extrabold font-mono text-rose-500" data-testid="report-total-expenses">{fmtCompact(t.expenses)}</div>
        </div>
        <div className="bg-card border border-border rounded-xl p-4 shadow-sm">
          <div className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Net Profit</div>
          <div className={`mt-1 text-2xl font-extrabold font-mono ${profitClass(t.profit)}`} data-testid="report-net-profit">{fmtCompact(t.profit)}</div>
        </div>
        <div className="bg-card border border-border rounded-xl p-4 shadow-sm">
          <div className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Trips · Qty (T)</div>
          <div className="mt-1 text-2xl font-extrabold font-mono text-foreground" data-testid="report-total-trips">{t.trips ?? 0}<span className="text-base text-muted-foreground"> · {t.quantity ?? 0}</span></div>
        </div>
      </div>

      <Tabs defaultValue="owner">
        <TabsList>
          <TabsTrigger value="owner" data-testid="tab-owner-performance">Owner Performance</TabsTrigger>
          <TabsTrigger value="truck" data-testid="tab-truck-performance">Truck Performance</TabsTrigger>
          <TabsTrigger value="hub" data-testid="tab-hub-profit">Hub Profitability</TabsTrigger>
          <TabsTrigger value="expenses" data-testid="tab-expense-breakdown">Expense Breakdown</TabsTrigger>
        </TabsList>

        <TabsContent value="owner" className="mt-4">
          <div className="bg-card border border-border rounded-xl shadow-sm overflow-hidden">
            <div className="p-4 border-b border-border flex items-center justify-between">
              <h3 className="font-display font-semibold">Owner Performance <span className="text-xs font-normal text-muted-foreground">· click a row for full detail</span></h3>
              <Button size="sm" variant="secondary" data-testid="export-owner-csv-button" onClick={exportOwner}><Download className="h-4 w-4 mr-1" />Export CSV</Button>
            </div>
            <OwnerKpis rows={data.owner_performance} />
            <div className="overflow-x-auto max-h-[560px]">
              {data.owner_performance.length === 0 ? <Empty /> : (
                <table className="w-full text-sm">
                  <thead className="bg-muted text-xs uppercase font-bold text-muted-foreground tracking-wider sticky top-0 z-10">
                    <tr>
                      <th className="py-3 px-4 text-left">Owner</th><th className="py-3 px-4 text-right">Trips</th>
                      <th className="py-3 px-4 text-right">Quantity (T)</th><th className="py-3 px-4 text-right">Revenue</th>
                      <th className="py-3 px-4 text-right">Commission</th>
                      <th className="py-3 px-4 text-right">Vouchers</th><th className="py-3 px-4 text-right">AdBlue</th>
                      <th className="py-3 px-4 text-right">Expenses</th><th className="py-3 px-4 text-right">Net</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.owner_performance.map((r, i) => (
                      <tr key={i} className={`border-t border-border/60 cursor-pointer ${i % 2 ? "row-odd" : "row-even"}`} onClick={() => setReportOwner(r.name)} data-testid={`owner-perf-row-${i}`}>
                        <td className="py-3 px-4">{r.name === "—" ? <span className="owner-badge bg-amber-500/15 text-amber-600 border border-amber-500/40" title="Truck not in Truck Master / no owner">Unassigned</span> : <span className="inline-flex items-center gap-2"><span className="h-7 w-7 rounded-full bg-primary/15 text-primary text-xs font-bold inline-flex items-center justify-center">{r.name.slice(0, 1).toUpperCase()}</span><span className="font-semibold">{r.name}</span></span>}</td>
                        <td className="py-3 px-4 text-right font-mono">{r.trips}</td>
                        <td className="py-3 px-4 text-right font-mono">{fmtNumber(r.quantity)}</td>
                        <td className="py-3 px-4 text-right font-mono">{fmtCurrencyFull(r.revenue)}</td>
                        <td className={`py-3 px-4 text-right font-mono font-semibold ${profitClass(r.commission)}`}>{fmtCurrencyFull(r.commission)}</td>
                        <td className="py-3 px-4 text-right font-mono text-rose-500">{fmtCurrencyFull(r.vouchers)}</td>
                        <td className="py-3 px-4 text-right font-mono text-rose-500">{fmtCurrencyFull(r.adblue)}</td>
                        <td className="py-3 px-4 text-right font-mono text-rose-500">{fmtCurrencyFull(r.expenses)}</td>
                        <td className="py-3 px-4 text-right"><span className={`font-mono font-bold px-2 py-1 rounded-md ${r.net < 0 ? "bg-rose-500/10 text-rose-500" : "bg-emerald-500/10 text-emerald-600"}`}>{fmtCurrencyFull(r.net)}</span></td>
                      </tr>
                    ))}
                  </tbody>
                  <OwnerTotals rows={data.owner_performance} />
                </table>
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="truck" className="mt-4">
          <div className="bg-card border border-border rounded-xl shadow-sm overflow-hidden">
            <div className="p-4 border-b border-border flex items-center justify-between">
              <h3 className="font-display font-semibold">Truck Performance</h3>
              <Button size="sm" variant="secondary" data-testid="export-truck-csv-button" onClick={exportTruck}><Download className="h-4 w-4 mr-1" />Export CSV</Button>
            </div>
            <div className="overflow-x-auto max-h-[560px]">
              {data.truck_performance.length === 0 ? <Empty /> : (
                <table className="w-full text-sm">
                  <thead className="bg-muted/40 text-xs uppercase font-bold text-muted-foreground tracking-wider sticky top-0">
                    <tr>
                      <th className="py-3 px-4 text-left">Vehicle</th><th className="py-3 px-4 text-left">Owner</th>
                      <th className="py-3 px-4 text-left">Type</th><th className="py-3 px-4 text-right">Trips</th>
                      <th className="py-3 px-4 text-right">Quantity (T)</th><th className="py-3 px-4 text-right">Revenue</th>
                      <th className="py-3 px-4 text-right">Commission</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.truck_performance.map((r, i) => (
                      <tr key={i} className="border-t border-border/60" data-testid={`truck-perf-row-${i}`}>
                        <td className="py-3 px-4 font-mono font-medium">{r.vehicle_number}</td>
                        <td className="py-3 px-4">{r.owner_name || "-"}</td>
                        <td className="py-3 px-4 text-muted-foreground">{r.vehicle_type || "-"}</td>
                        <td className="py-3 px-4 text-right font-mono">{r.trips}</td>
                        <td className="py-3 px-4 text-right font-mono">{r.quantity}</td>
                        <td className="py-3 px-4 text-right font-mono">{fmtCurrencyFull(r.revenue)}</td>
                        <td className={`py-3 px-4 text-right font-mono font-semibold ${profitClass(r.commission)}`}>{fmtCurrencyFull(r.commission)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="hub" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-card border border-border rounded-xl p-5 shadow-sm">
              <h3 className="font-display font-semibold mb-4">Hub Revenue vs Expenses</h3>
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={data.hub_profit}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="hub" stroke="hsl(var(--muted-foreground))" tickLine={false} axisLine={false} />
                  <YAxis stroke="hsl(var(--muted-foreground))" tickLine={false} axisLine={false} tickFormatter={fmtCompact} width={60} />
                  <Tooltip formatter={(v) => fmtCurrencyFull(v)} />
                  <Legend />
                  <Bar dataKey="revenue" fill="#10b981" radius={[6, 6, 0, 0]} />
                  <Bar dataKey="expenses" fill="#f43f5e" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="bg-card border border-border rounded-xl shadow-sm overflow-hidden">
              <div className="p-4 border-b border-border flex items-center justify-between">
                <h3 className="font-display font-semibold">Profitability</h3>
                <Button size="sm" variant="secondary" data-testid="export-hub-csv-button" onClick={exportHub}><Download className="h-4 w-4 mr-1" />Export</Button>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-muted/40 text-xs uppercase font-bold text-muted-foreground tracking-wider">
                  <tr><th className="py-3 px-4 text-left">Hub</th><th className="py-3 px-4 text-right">Revenue</th><th className="py-3 px-4 text-right">Expenses</th><th className="py-3 px-4 text-right">Profit</th></tr>
                </thead>
                <tbody>
                  {data.hub_profit.map((h, i) => (
                    <tr key={i} className="border-t border-border/60">
                      <td className="py-3 px-4 font-medium">{h.hub}</td>
                      <td className="py-3 px-4 text-right font-mono">{fmtCurrencyFull(h.revenue)}</td>
                      <td className="py-3 px-4 text-right font-mono">{fmtCurrencyFull(h.expenses)}</td>
                      <td className={`py-3 px-4 text-right font-mono font-semibold ${profitClass(h.profit)}`}>{fmtCurrencyFull(h.profit)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="expenses" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-card border border-border rounded-xl p-5 shadow-sm">
              <h3 className="font-display font-semibold mb-4">By Category</h3>
              {data.expense_breakdown.length === 0 ? <Empty /> : (
                <ResponsiveContainer width="100%" height={320}>
                  <PieChart>
                    <Pie data={data.expense_breakdown} dataKey="value" nameKey="name" innerRadius={60} outerRadius={110} paddingAngle={3}>
                      {data.expense_breakdown.map((e, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={(v) => fmtCurrencyFull(v)} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
            <div className="bg-card border border-border rounded-xl shadow-sm overflow-hidden">
              <div className="p-4 border-b border-border"><h3 className="font-display font-semibold">Category Totals</h3></div>
              <table className="w-full text-sm">
                <thead className="bg-muted/40 text-xs uppercase font-bold text-muted-foreground tracking-wider">
                  <tr><th className="py-3 px-4 text-left">Category</th><th className="py-3 px-4 text-right">Amount</th></tr>
                </thead>
                <tbody>
                  {data.expense_breakdown.map((e, i) => (
                    <tr key={i} className="border-t border-border/60">
                      <td className="py-3 px-4 font-medium flex items-center gap-2">
                        <span className="h-3 w-3 rounded-sm" style={{ background: CHART_COLORS[i % CHART_COLORS.length] }} />{e.name}
                      </td>
                      <td className="py-3 px-4 text-right font-mono">{fmtCurrencyFull(e.value)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>
      </Tabs>
      {reportOwner && <OwnerStatementDialog owner={reportOwner} filters={filters} onClose={() => setReportOwner(null)} />}
    </div>
  );
}

